import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChalkboardTeacher, ArrowsClockwise, DownloadSimple, Eye, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { useAuth } from "../../../contexts/AuthContext";
import { hasFeature } from "../../../lib/features";
import { Pill } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { LibraryPreview, err } from "./libraryUtils";

/** H&S Overview: this week's suggested toolbox talk, rotating through library talks this workspace hasn't delivered yet. */
export function WeeklyTalkCard({ canEdit }) {
  const { user } = useAuth();
  const on = hasFeature(user, "hs_library");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!on || !canEdit) return;
    API.get("/hs/library/weekly-talk", { params: { offset } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [on, canEdit, offset]);

  if (!on || !canEdit || data?.error) return null;
  const t = data?.talk;

  const use = async () => {
    if (t.draft_id) { navigate(`/health-safety?tab=toolbox&talk=${t.draft_id}`); return; }
    setBusy(true);
    try {
      const res = (await API.post(`/hs/library/${t.id}/use`)).data;
      toast.success("Talk added as a draft — deliver it and record who attended");
      navigate(res.url);
    } catch (e) { err(e, "Couldn't copy the talk"); } finally { setBusy(false); }
  };
  const openPreview = async () => {
    try { setPreview((await API.get(`/hs/library/${t.id}`)).data); } catch (e) { err(e, "Couldn't open the preview"); }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="hs-overview-weekly-talk">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 flex-wrap"><ChalkboardTeacher size={16} weight="fill" className="text-primary" /> This week's toolbox talk
        {data && <span className="text-[11px] font-normal text-muted-foreground" data-testid="hs-overview-weekly-talk-week">{data.week}</span>}
        {data && data.total > 0 && <Pill tone={data.remaining === 0 ? TONE_CLASSES.ok : TONE_CLASSES.none} testid="hs-overview-weekly-talk-progress">{data.delivered} of {data.total} library talks delivered</Pill>}
      </h3>
      {data === null ? <p className="text-xs text-muted-foreground">Loading…</p> : !t ? (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="hs-overview-weekly-talk-done"><CheckCircle size={14} className="text-emerald-600" /> {data.total === 0 ? "No talks in the library yet." : "You've delivered every talk in the library — nice work. New ones will appear here as they're published."}</p>
      ) : (
        <>
          <p className="text-sm font-medium leading-snug" data-testid="hs-overview-weekly-talk-title">{t.title}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{t.trade} · about {t.words} words · {Math.max(5, Math.round(t.words / 60) * 2)} min to deliver{t.draft_id ? " · already in your workspace as a draft" : ""}</p>
          <p className="text-xs text-muted-foreground mt-2 line-clamp-3 whitespace-pre-line" data-testid="hs-overview-weekly-talk-summary">{t.summary}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" className="h-7 text-xs" disabled={!canEdit || busy} onClick={use} data-testid="hs-overview-weekly-talk-use">
              {t.draft_id ? <><ArrowRight size={12} className="mr-1" /> Open your draft</> : <><DownloadSimple size={12} className="mr-1" /> Use this week's talk</>}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openPreview} data-testid="hs-overview-weekly-talk-preview"><Eye size={12} className="mr-1" /> Read it</Button>
            {data.remaining > 1 && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOffset((o) => o + 1)} data-testid="hs-overview-weekly-talk-another"><ArrowsClockwise size={12} className="mr-1" /> Show another</Button>}
          </div>
        </>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl sm:max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden break-words grid-cols-[minmax(0,1fr)]" data-testid="hs-overview-weekly-talk-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle>{preview.title}</DialogTitle>
                <DialogDescription>Toolbox talk · {preview.trade} · v{preview.version}</DialogDescription>
              </DialogHeader>
              <LibraryPreview item={preview} />
              <DialogFooter className="gap-2 sm:gap-0 sticky -bottom-6 -mx-6 px-6 py-3 bg-background border-t border-border">
                <Button variant="outline" onClick={() => setPreview(null)} data-testid="hs-overview-weekly-talk-dialog-close">Close</Button>
                <Button disabled={!canEdit || busy} onClick={() => { setPreview(null); use(); }} data-testid="hs-overview-weekly-talk-dialog-use">{t?.draft_id ? "Open your draft" : "Use this week's talk"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
